import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  getMaskedPhoneNumber,
  getPhoneMask,
} from '~shared/lib/format-phone-number';
import { InputOtp } from '~shared/react-hook-form-adapters/input-otp';

import { ButtonLink } from '@platform-ui/button';
import { Box, Col } from '@platform-ui/layout';
import Text from '@platform-ui/text';

import { useAtom, useAtomValue } from 'jotai';

import { ResendButton } from './resend-button';

import {
  getAttemptsLimitMessage,
  getOtpRequestDelaySeconds,
  useResendCountdown,
} from '../../lib';
import { isAttemptsLimitReachedAtom, lastOtpRequestAtom } from '../../model';

type FormValues = {
  confirmationCode: string;
};

export type OtpConfirmationError = 'confirmationError' | 'unexpectedError';

export type Props = {
  phoneNumber: string;
  onCodeResend: () => Promise<void>;
  onCodeResendLimitError: () => void;
  onCodeSubmit: (code: string) => void;
  onNumberChange: () => void;
  codeChecking: boolean;
  codeSubmitError?: OtpConfirmationError;
};

export function OtpConfirmation({
  phoneNumber,
  onCodeResend,
  onCodeSubmit,
  onCodeResendLimitError,
  onNumberChange,
  codeChecking,
  codeSubmitError,
}: Props): JSX.Element {
  const { control, handleSubmit } = useForm<FormValues>({
    mode: 'all',
    shouldUnregister: true,
  });
  const [lastOtpRequest, setLastOtpRequest] = useAtom(lastOtpRequestAtom);
  const isAttemptsLimitReached = useAtomValue(isAttemptsLimitReachedAtom);
  const [timer, startCountdown] = useResendCountdown();

  useEffect(() => {
    const delay = getOtpRequestDelaySeconds(lastOtpRequest);

    if (delay) {
      startCountdown(delay);
    }
  }, [lastOtpRequest, startCountdown]);

  const getConfirmationErrorMessage = () => {
    if (codeSubmitError === 'unexpectedError') {
      return 'Error checking the code';
    }
    if (codeSubmitError === 'confirmationError') {
      return 'Wrong code';
    }
  };

  const handleCodeResend = async () => {
    await onCodeResend();
    setLastOtpRequest({ phone: phoneNumber, time: new Date().getTime() });
  };

  const handleOnCodeResendLimitError = async () => {
    await onCodeResendLimitError();
    setLastOtpRequest({ phone: phoneNumber, time: new Date().getTime() });
  };

  const showResendButton = !isAttemptsLimitReached || !timer;
  const showAttemptsLimitError = isAttemptsLimitReached && !!timer;

  return (
    <Col gaps={16} alignCross="stretch">
      <Col gaps={4}>
        <Text color="rgba(0, 0, 0, 0.8)" size={15}>
          We sent a&nbsp;confirmation code
        </Text>
        <Text color="rgba(0, 0, 0, 0.8)" size={15}>
          {getMaskedPhoneNumber(getPhoneMask(phoneNumber), phoneNumber)}
        </Text>
      </Col>
      <Box width={228}>
        <form
          onSubmit={handleSubmit(({ confirmationCode }) =>
            onCodeSubmit(confirmationCode)
          )}
        >
          <Controller
            name="confirmationCode"
            control={control}
            rules={{
              required: 'Please, enter the code',
              pattern: {
                value: /^\d{6}$/,
                message: 'The code should have 6 digits',
              },
            }}
            render={(props) => (
              <InputOtp
                {...props}
                autoFocus
                label="SMS code"
                autoComplete="off"
                checking={codeChecking}
                confirmationError={getConfirmationErrorMessage()}
                onFieldDone={(_event, { value }) => onCodeSubmit(value)}
              />
            )}
          />
        </form>
      </Box>
      <Col gaps={16} alignCross="start">
        {showAttemptsLimitError && (
          <Text color="rgb(227, 28, 28)">{getAttemptsLimitMessage(timer)}</Text>
        )}
        {showResendButton && (
          <ResendButton
            timer={timer}
            disabled={codeChecking}
            onResend={handleCodeResend}
            onResendLimitError={handleOnCodeResendLimitError}
          />
        )}

        <ButtonLink
          size="s"
          disableHorizontalPadding
          disableVerticalPadding
          disabled={codeChecking}
          onClick={onNumberChange}
        >
          Change mobile number
        </ButtonLink>
      </Col>
    </Col>
  );
}
